import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDateFormats } from '../../../hooks/useDateFormats';
import { DateFormats, DateFormatConfig } from '../../../config/dateFormats';
import dayjs from 'dayjs';
import { getCompanies } from '../../../api/General/Company';
import { CompanyView, Roles } from '../../../models/general';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { PageLayout } from '../../../components/ui/PageLayout';
import { CalendarDays } from 'lucide-react';

const DateFormatConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();
  const {
    formats,
    config,
    isLoading,
    error,
    updateConfig,
    resetToDefault,
    setToUS,
    setToISO,
    clearError,
    companyId,
    setCompanyId,
  } = useDateFormats();

  const [selectedConfig, setSelectedConfig] = useState<DateFormatConfig>(config);
  const [companies, setCompanies] = useState<CompanyView[]>([]);
  const userRole = useSelector((state: any) => state.user?.role);
  const isOperator = userRole === Roles.Operator || userRole === 'Operator';
  const [customFormats, setCustomFormats] = useState<DateFormats>({
    date: {
      display: 'DD/MM/YYYY',
      input: 'YYYY-MM-DD',
      api: 'YYYY-MM-DD',
      short: 'DD/MM',
      long: 'DD MMMM YYYY'
    },
    time: {
      display: 'HH:mm',
      input: 'HH:mm',
      short: 'HH:mm',
      long: 'HH:mm:ss',
      ampm: 'hh:mm A'
    },
    datetime: {
      display: 'DD/MM/YYYY HH:mm',
      input: 'YYYY-MM-DDTHH:mm',
      api: 'YYYY-MM-DDTHH:mm:ssZ',
      short: 'DD/MM HH:mm',
      long: 'DD MMMM YYYY HH:mm:ss'
    },
    specialized: {
      monthYear: 'MM/YYYY',
      dayMonth: 'DD MMM',
      weekday: 'dddd',
      relativeDays: 'DD/MM'
    }
  });

  const handleConfigChange = (value: string) => {
    setSelectedConfig(value as DateFormatConfig);
  };

  const handleCustomFormatChange = (
    category: 'date' | 'time' | 'datetime' | 'specialized',
    field: string,
    value: string
  ) => {
    setCustomFormats(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleApply = async () => {
    const fmts = selectedConfig === 'custom' ? customFormats : undefined;
    const success = await updateConfig(selectedConfig, fmts);
    if (success) {
      showToast(t('Date format configuration saved successfully!'), 'success');
    }
  };

  const handleQuickAction = async (action: () => Promise<boolean>) => {
    const success = await action();
    if (success) {
      setSelectedConfig(config);
      showToast(t('Date format configuration saved successfully!'), 'success');
    }
  };

  const previewDate = dayjs();
  const getPreviewFormat = (format: string) => {
    try {
      return previewDate.format(format);
    } catch {
      return t('Invalid format');
    }
  };

  const formatConfigs = [
    { value: 'default' as DateFormatConfig, label: t('Default Format'), description: 'DD/MM/YYYY, HH:mm' },
    { value: 'us' as DateFormatConfig, label: t('US Format'), description: 'MM/DD/YYYY, hh:mm A' },
    { value: 'iso' as DateFormatConfig, label: t('ISO Format'), description: 'YYYY-MM-DD, HH:mm' },
    { value: 'custom' as DateFormatConfig, label: t('Custom Format'), description: t('Define your own formats') },
  ];

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const companiesRes = await getCompanies();
        setCompanies(companiesRes.payload || []);
        if (!companyId && companiesRes.payload && companiesRes.payload.length > 0) {
          setCompanyId(companiesRes.payload[0].id);
        }
      } catch {
        // ignore
      }
    };
    if (isOperator) {
      loadCompanies();
    }
  }, [isOperator]);

  useEffect(() => {
    setSelectedConfig(config);
  }, [config]);

  const companyOptions = companies.map(c => ({ value: c.id, label: c.name }));

  return (
    <PageLayout
      title={t('Date Format Configuration')}
      description={t('Configure how dates and times are displayed throughout the application for your company')}
      icon={CalendarDays}
      accentColor="slate"
      maxWidth="5xl"
    >
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
        {error && (
          <div role="alert" className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive mb-4 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={clearError} className="text-destructive hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm">
              &times;
            </button>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-2">
            <Spinner size="sm" />
            <span className="text-sm text-muted-foreground">{t('Saving configuration...')}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {isOperator && (
            <div className="col-span-full">
              <Select
                label={t('Select Company')}
                options={companyOptions}
                value={companyId}
                onChange={(val: any) => setCompanyId(val)}
                searchable
              />
            </div>
          )}

          <div>
            <fieldset disabled={isLoading}>
              <legend className="text-lg font-semibold text-foreground mb-3">{t('Format Configuration')}</legend>
              <div className="space-y-3">
                {formatConfigs.map((fc) => (
                  <label
                    key={fc.value}
                    className="flex items-start gap-3 rounded-lg border border-border p-4 cursor-pointer has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                  >
                    <input
                      type="radio"
                      name="dateFormat"
                      value={fc.value}
                      checked={selectedConfig === fc.value}
                      onChange={() => handleConfigChange(fc.value)}
                      className="mt-0.5 accent-primary"
                      disabled={isLoading}
                    />
                    <div>
                      <span className="text-sm font-medium text-foreground">{fc.label}</span>
                      <p className="text-sm text-muted-foreground">{fc.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">{t('Current Preview')}</h2>

            <div className="mb-4">
              <h3 className="text-sm font-medium text-foreground mb-1">{t('Date Formats')}:</h3>
              <div className="flex flex-wrap gap-1 mb-1">
                <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-foreground">{t('Display')}: {getPreviewFormat(formats.date.display)}</span>
                <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-foreground">{t('Short')}: {getPreviewFormat(formats.date.short)}</span>
                <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-foreground">{t('Long')}: {getPreviewFormat(formats.date.long)}</span>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="text-sm font-medium text-foreground mb-1">{t('Time Formats')}:</h3>
              <div className="flex flex-wrap gap-1 mb-1">
                <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-foreground">{t('Display')}: {getPreviewFormat(formats.time.display)}</span>
                <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-foreground">{t('Short')}: {getPreviewFormat(formats.time.short)}</span>
                <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-foreground">{t('AM/PM')}: {getPreviewFormat(formats.time.ampm)}</span>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="text-sm font-medium text-foreground mb-1">{t('DateTime Formats')}:</h3>
              <div className="flex flex-wrap gap-1 mb-1">
                <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-foreground">{t('Display')}: {getPreviewFormat(formats.datetime.display)}</span>
                <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-foreground">{t('Short')}: {getPreviewFormat(formats.datetime.short)}</span>
              </div>
            </div>
          </div>
        </div>

        {selectedConfig === 'custom' && (
          <>
            <hr className="border-border my-6" />
            <h2 className="text-lg font-semibold text-foreground mb-3">{t('Custom Format Configuration')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {(['date', 'time', 'datetime', 'specialized'] as const).map((category) => (
                <div key={category}>
                  <h3 className="text-sm font-medium text-foreground mb-2">
                    {t(category.charAt(0).toUpperCase() + category.slice(1) + ' Formats')}
                  </h3>
                  {Object.entries(customFormats[category]).map(([field, value]) => (
                    <div key={field} className="mb-3">
                      <Input
                        label={t(field.charAt(0).toUpperCase() + field.slice(1))}
                        value={value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCustomFormatChange(category, field, e.target.value)}
                        disabled={isLoading}
                        description={`Preview: ${getPreviewFormat(value)}`}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}

        <hr className="border-border my-6" />

        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={() => handleQuickAction(resetToDefault)} disabled={isLoading}>
            {t('Reset to Default')}
          </Button>
          <Button variant="outline" onClick={() => handleQuickAction(setToUS)} disabled={isLoading}>
            {t('Set to US Format')}
          </Button>
          <Button variant="outline" onClick={() => handleQuickAction(setToISO)} disabled={isLoading}>
            {t('Set to ISO Format')}
          </Button>
          <Button onClick={handleApply} disabled={isLoading}>
            {isLoading ? <Spinner size="sm" /> : t('Apply Configuration')}
          </Button>
        </div>
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default DateFormatConfiguration;
